import React, { useContext, useState } from 'react';
import { InputField } from '@trp-beacon-ui-kit/react-controls';
import { FlowContext } from '../../core/flow-engine';
import styles from './style.css';
import {
  getTaxableProfileHeaderText,
  getURLTickers,
  renderPrimaryEmailTitleWithTooltip,
  renderAmountTitleWithTooltip
} from './util';
import { isEmptyObject, withDefault } from '../../core/util';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import Heading from '../../components/Typography/Heading/Heading';
import LoginCTA from '../../components/LoginCTA/LoginCTA';
import {
  accountTypes,
  handleInputFormat,
  addCommas
} from '../../flows/shared/utils';
import ProfileScreenButtons from '../../components/Button/ProfileScreenButtons';
import {
  firstnameValidation,
  lastnameValidation,
  restrictedFirstNameValidation,
  restrictedLastNameValidation
} from '../../flows/shared/validations';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';
import ExistingAgreementsBlockModal from '../../components/ExistingAgreementModals/ExistingAgreementsBlockModal';
import { isNonTransferIndividualOrTOD } from '../PersonalInformationScreen/util';

/* eslint-disable complexity */
const TaxableProfileScreen = () => {
  const { formProps, appState, flowProps, back } = useContext(FlowContext);
  const {
    store: {
      authenticated,
      enabled,
      prospect,
      app,
      amount,
      accountType,
      chargeType,
      existingAgreements
    }
  } = appState;

  const { trustee, customer } = app;

  const [amountValue, setAmountValue] = useState(withDefault(amount, ''));

  // US661619 (COMPLIANCE) - block clients opening agreements for Individual and TOD
  // that match an existing agreement
  const hasExistingAgree =
    !isEmptyObject(existingAgreements) &&
    isNonTransferIndividualOrTOD(accountType, chargeType);

  // these will exist if user is authenticated or a RJ user
  // pull data for client from trustee object if TRUST; otherwise use customer object
  const firstName =
    accountType === accountTypes.TRUST
      ? trustee?.name?.firstName
      : customer?.name?.firstName;
  const lastName =
    accountType === accountTypes.TRUST
      ? trustee?.name?.lastName
      : customer?.name?.lastName;
  const primaryEmailAddress =
    accountType === accountTypes.TRUST
      ? trustee?.primaryEmailAddress
      : customer?.primaryEmailAddress;

  // multi-fund buy query check
  // capture tickers to pass to InvestmentReviewScreen
  const multiBuyTickers = getURLTickers(window.location.search);

  return (
    <div className="profile-fields">
      <Heading
        text={getTaxableProfileHeaderText(
          authenticated,
          firstName,
          accountType,
          chargeType
        )}
      />

      <LoginCTA authenticated={authenticated} />

      <div className="name-fields">
        <div data-e2e="firstname" className="name-column">
          <InputField
            type="text"
            title="First Name"
            {...formProps.firstName}
            data-name={generateGeneralTestID(
              'BasicPI',
              'AccountOwnerFirstName'
            )}
            disabled={enabled}
            defaultValue={firstName}
            maxLength="35"
            validation={
              enabled ? firstnameValidation : restrictedFirstNameValidation
            }
            className="blocked_data"
          />
        </div>
        <div data-e2e="lastname" className="name-column">
          <InputField
            type="text"
            title="Last Name"
            {...formProps.lastName}
            data-name={generateGeneralTestID('BasicPI', 'AccountOwnerLastName')}
            disabled={enabled}
            defaultValue={lastName}
            maxLength="35"
            validation={
              enabled ? lastnameValidation : restrictedLastNameValidation
            }
            className="blocked_data"
          />
        </div>
      </div>
      <div data-e2e="email">
        <InputField
          type="email"
          title={renderPrimaryEmailTitleWithTooltip()}
          {...formProps.primaryEmailAddress}
          data-name={generateGeneralTestID('BasicPI', 'AccountOwnerEmail')}
          defaultValue={primaryEmailAddress}
          disabled={
            prospect
          } /* disable email fields for prospect due to email verification */
          className="blocked_data"
        />
      </div>
      <div data-e2e="amount">
        <InputField
          onChange={evt =>
            handleInputFormat(evt, 'DECIMAL_CURRENCY', setAmountValue)
          }
          maxLength="18"
          {...formProps.amount}
          data-name={generateGeneralTestID('BasicPI', 'InvestmentAmount')}
          value={addCommas(amountValue)}
          title={renderAmountTitleWithTooltip(accountType)}
        />
      </div>

      <ProfileScreenButtons
        multiBuyTickers={multiBuyTickers}
        {...(!flowProps?.fundLaterFlow && { handleBackClick: back('landing') })}
      />

      <PhoneNumber screen="default" />
      <style jsx>{styles}</style>

      {/* block account opening if existing agreement flag is true */}
      {hasExistingAgree && <ExistingAgreementsBlockModal />}
    </div>
  );
};
/* eslint-enable complexity */

export default TaxableProfileScreen;
